/**
 * Which protocol text the CRF workspace should carry, wherever it came from.
 *
 * `generateCRFVariables` refuses a call without `protocolText`, so a workspace
 * that loads without it cannot generate a single variable — and without
 * variables there is no Data Dictionary, and without that none of the study
 * documents downstream can be generated at all.
 *
 * On 2026-08-26 that happened on a real study: 0 of 46 forms processed, every
 * one failing with "formName and protocolText are required", because the
 * branch a freshly generated CRF list lands in never resolved the text. The
 * sibling branch did, and said in its own comment that it was there to stop
 * exactly this. One fix, one of two paths.
 *
 * The choice lives here so both paths make it identically and so it can be
 * tested at all — `web/` has no test runner, and a React load path cannot be
 * reached from a unit test.
 */

/**
 * How much rebuilt text is worth sending.
 *
 * A rebuild from the stored analysis can produce a near-empty string when the
 * analysis is thin. Sending it turns a clear "protocolText is required" into a
 * vaguer failure deeper in the generator, so it is rejected here instead.
 */
export const MIN_REBUILT_PROTOCOL_TEXT = 50;

export function chooseProtocolText(cached: unknown, rebuilt: unknown): string {
  const stored = typeof cached === 'string' ? cached.trim() : '';
  if (stored.length > 0) return typeof cached === 'string' ? cached : '';

  const fallback = typeof rebuilt === 'string' ? rebuilt : '';
  return fallback.trim().length > MIN_REBUILT_PROTOCOL_TEXT ? fallback : '';
}
